/**
 * Shared subscriber for the presence-related WS events consumed by supervisor
 * "live" views. Debounces bursts (many reps changing status in quick
 * succession) so the view only re-hydrates once per burst.
 *
 * Returns a single unsubscribe fn that tears down every underlying listener
 * and clears the pending debounce timer — safe to call in a `useEffect`
 * cleanup.
 */

type SubscribeFn = (event: string, handler: (msg: unknown) => void) => () => void;

/**
 * The WS event types that signal a change in presence state — any rep
 * clocking in/out, changing status, picking up or finishing a call, or
 * dropping off the heartbeat sweep. `rep_call_changed` is fired from the
 * voice-event-consumer when `current_call_id` flips.
 */
export const KPI_LIVE_EVENTS = [
  'rep_status_changed',
  'session_started',
  'session_ended',
  'rep_went_offline',
  'rep_call_changed',
] as const;

export function subscribeToKpiLiveEvents(
  subscribe: SubscribeFn,
  reload: () => void,
  debounceMs = 250,
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const trigger = (): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      reload();
    }, debounceMs);
  };
  const unsubs = KPI_LIVE_EVENTS.map((event) => subscribe(event, trigger));
  return () => {
    if (timer) clearTimeout(timer);
    timer = null;
    unsubs.forEach((unsub) => unsub());
  };
}
